/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import { isVariableRef } from '../variables/variables-util';

/**
 * @import {
 * BooleanExpression,
 * StringExpression,
 * StringSetExpression,
 * TrackConditions,
 * UnknownNode
 * } from '.'
 * @import { VariableRef } from '../variables'
 */

/** @type {Array<'AND' | 'OR'>} */
export const logicalTypes = ['AND', 'OR'];

/** @type {Array<'EQ' | 'NE' | 'CONTAINS' | 'GTCN' | 'LTCN' | 'GECN' | 'LECN'>} */
export const scalarComparisonTypes = [
  'EQ',
  'NE',
  'CONTAINS',
  'GTCN',
  'LTCN',
  'GECN',
  'LECN'
];

/** @type {Array<'IN'>} */
export const listMembershipTypes = ['IN'];

/** @type {Array<'TRUE' | 'FALSE'>} */
export const constantPredicateTypes = ['TRUE', 'FALSE'];

const parseStringBinary = parseBinaryShape(
  parseStringExpression,
  parseStringExpression
);
const parseStringSetBinary = parseBinaryShape(
  parseStringSetExpression,
  parseStringSetExpression
);
const parseStringToSetBinary = parseBinaryShape(
  parseStringExpression,
  parseStringSetExpression
);

/** @type {Record<string, (json: any) => BooleanExpression>} */
const booleanExpressionsParsers = {
  AND: parseNaryShape,
  OR: parseNaryShape,
  NOT: parseUnaryShape,
  TRYB: parseTryShape,
  EQ: parseStringBinary,
  NE: parseStringBinary,
  CONTAINS: parseStringBinary,
  GTCN: parseStringBinary,
  LTCN: parseStringBinary,
  GECN: parseStringBinary,
  LECN: parseStringBinary,
  IN: parseStringToSetBinary,
  ALLOF: parseStringSetBinary,
  ANYOF: parseStringSetBinary,
  TRUE: parseLiteralShape,
  FALSE: parseLiteralShape
};

/**
 * @param {any} value
 * @param {string} label
 */
function assertObject(value, label) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`Expected ${label} object, got ${typeof value}`);
  }
}

/**
 * @param {Object} json
 * @returns {TrackConditions | undefined}
 * @throws {TypeError}
 */
export function parse(json) {
  if (isNil(json) || isEmpty(json)) {
    return undefined;
  }
  return parseConditions(json);
}

/**
 * @param {(json: any, side: string) => any} lhsParser
 * @param {(json: any, side: string) => any} rhsParser
 * @returns {(json: any) => BooleanExpression}
 */
function parseBinaryShape(lhsParser, rhsParser) {
  return (json) => ({
    type: json.type,
    lhs: lhsParser(json.lhs, 'lhs'),
    rhs: rhsParser(json.rhs, 'rhs')
  });
}

/**
 * @param {any} json
 * @returns {BooleanExpression}
 */
function parseBooleanExpression(json) {
  assertObject(json, 'condition');

  const parser = booleanExpressionsParsers[json.type];
  if (!parser) {
    return {
      type: 'UNKNOWN',
      originalType: String(json.type || 'missing_type')
    };
  }

  return parser(json);
}

/**
 * @param {any} json
 * @returns {TrackConditions}
 */
function parseConditions(json) {
  assertObject(json, 'track conditions');

  if (typeof json.fallback !== 'boolean') {
    throw new TypeError('Track conditions fallback must be boolean');
  }

  switch (json.v) {
    case 'v1':
      if (isNil(json.root)) {
        throw new TypeError('Track conditions root is required for v1');
      }
      return {
        v: json.v,
        fallback: json.fallback,
        root: parseBooleanExpression(json.root)
      };
    default:
      // Pre neznámu major verziu neparsujeme root, iba fallback
      return {
        v: json.v,
        fallback: json.fallback,
        root: undefined
      };
  }
}

/**
 * @param {any & { type: 'AND' | 'OR' }} json
 * @returns {BooleanExpression}
 */
function parseNaryShape(json) {
  const { args } = json;
  if (!Array.isArray(args)) {
    throw new TypeError(
      `Boolean nary operator ${json.type} requires an array args`
    );
  }
  return { type: json.type, args: args.map(parseBooleanExpression) };
}

/**
 * @param {any} json
 * @param {string} side
 * @returns {StringExpression}
 */
function parseStringExpression(json, side) {
  assertObject(json, `${side} scalar operand`);

  if (json.type === 'VAR') {
    return { type: 'VAR', name: parseVariableRef(json.name, side) };
  }
  if (json.type === 'CONST') {
    if (!isString(json.val)) {
      throw new TypeError(
        `${side} scalar const value must be string, got ${typeof json.val}`
      );
    }
    return { type: 'CONST', val: json.val };
  }
  if (json.type === 'TRY') {
    return {
      type: 'TRY',
      arg: parseStringExpression(json.arg, 'arg'),
      def: parseStringExpression(json.def, 'def')
    };
  }
  return {
    type: 'UNKNOWN',
    originalType: String(json.type || 'missing_type')
  };
}

/**
 * @param {any} json
 * @param {string} side
 * @returns {StringSetExpression}
 */
function parseStringSetExpression(json, side) {
  assertObject(json, `${side} plural operand`);

  if (json.type === 'VARS') {
    return { type: 'VARS', name: parseVariableRef(json.name, side) };
  }
  if (json.type === 'CONSTS') {
    if (!Array.isArray(json.val) || !json.val.every(isString)) {
      throw new TypeError(`${side} plural const value must be string[]`);
    }
    return { type: 'CONSTS', val: json.val };
  }
  if (json.type === 'TRYS') {
    return {
      type: 'TRYS',
      arg: parseStringSetExpression(json.arg, 'arg'),
      def: parseStringSetExpression(json.def, 'def')
    };
  }
  return {
    type: 'UNKNOWN',
    originalType: String(json.type || 'missing_type')
  };
}

/**
 * @param {any & { type: 'TRYB' }} json
 * @returns {BooleanExpression}
 */
function parseTryShape(json) {
  return {
    type: 'TRYB',
    arg: parseBooleanExpression(json.arg),
    def: parseBooleanExpression(json.def)
  };
}

/**
 * @param {any & { type: 'NOT' }} json
 * @returns {BooleanExpression}
 */
function parseUnaryShape(json) {
  return {
    type: 'NOT',
    arg: parseBooleanExpression(json.arg)
  };
}

/**
 * @param {any & { type: 'TRUE' | 'FALSE' }} json
 * @returns {BooleanExpression}
 */
function parseLiteralShape(json) {
  return { type: json.type };
}

/**
 * @param {any} value
 * @param {string} side
 * @returns {VariableRef}
 */
function parseVariableRef(value, side) {
  if (!isString(value)) {
    throw new TypeError(`${side} variable name must be string`);
  }
  if (!isVariableRef(value)) {
    throw new TypeError(`Unsupported namespace for variable: ${value}`);
  }
  return /** @type {VariableRef} */ (value);
}
